import React, { useState, useEffect, useMemo } from "react";
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Alert,
    Divider,
    Chip,
} from "@mui/material";
import {
    AttachMoney,
    Receipt,
    TrendingUp,
    Category as CategoryIcon,
    Payment,
} from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { ExpenseStatisticsResponse, ExpenseSearchParams } from "../../types";
import { formatCurrencyBDT } from "../../utils/currency";
import { expenseService } from "../../services/expenseService";

interface StatCardProps {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({
    title,
    value,
    icon,
    color,
    subtitle,
}) => (
    <Card sx={{ height: "100%" }}>
        <CardContent>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                }}
            >
                <Box
                    sx={{
                        backgroundColor: `${color}.light`,
                        borderRadius: 2,
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {icon}
                </Box>
            </Box>
            <Typography
                variant="h4"
                component="div"
                sx={{ fontWeight: "bold", mb: 1 }}
            >
                {value}
            </Typography>
            <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
            >
                {title}
            </Typography>
            {subtitle && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                >
                    {subtitle}
                </Typography>
            )}
        </CardContent>
    </Card>
);

const getSearchParamsFromUrl = (
    searchParams: URLSearchParams,
): ExpenseSearchParams => {
    const params: ExpenseSearchParams = {};

    const searchQuery = searchParams.get("search_query");
    if (searchQuery) params.search_query = searchQuery;

    const categoryIds = searchParams.get("category_ids");
    if (categoryIds) params.category_ids = categoryIds.split(",");

    const paymentMethod = searchParams.get("payment_method");
    if (paymentMethod) params.payment_method = paymentMethod as any;

    const expenseDateFrom = searchParams.get("expense_date_from");
    if (expenseDateFrom) params.expense_date_from = expenseDateFrom;

    const expenseDateTo = searchParams.get("expense_date_to");
    if (expenseDateTo) params.expense_date_to = expenseDateTo;

    const amountMin = searchParams.get("amount_min");
    if (amountMin) params.amount_min = parseFloat(amountMin);

    const amountMax = searchParams.get("amount_max");
    if (amountMax) params.amount_max = parseFloat(amountMax);

    return params;
};

export const ExpenseStatistics: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [stats, setStats] = useState<ExpenseStatisticsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const expenseSearchParams = useMemo(
        () => getSearchParamsFromUrl(searchParams),
        [searchParams],
    );

    useEffect(() => {
        const fetchStats = async () => {
            setLoading(true);
            setError(null);
            try {
                const data =
                    await expenseService.getExpenseStatistics(
                        expenseSearchParams,
                    );
                setStats(data);
            } catch (err: any) {
                console.error("Failed to load expense statistics:", err);
                setError(
                    err.response?.data?.detail ||
                        err.message ||
                        "Failed to load statistics",
                );
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [expenseSearchParams]);

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "60vh",
                    }}
                >
                    <CircularProgress size={60} />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    if (!stats) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Alert severity="info">No statistics available</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Expense Statistics
            </Typography>

            {/* Overview Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatCard
                        title="Total Expenses"
                        value={stats.total_expenses}
                        icon={<Receipt sx={{ color: "primary.main" }} />}
                        color="primary"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatCard
                        title="Total Amount"
                        value={formatCurrencyBDT(stats.total_amount)}
                        icon={<AttachMoney sx={{ color: "success.main" }} />}
                        color="success"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatCard
                        title="Average Expense"
                        value={formatCurrencyBDT(stats.average_expense)}
                        icon={<TrendingUp sx={{ color: "info.main" }} />}
                        color="info"
                    />
                </Grid>
            </Grid>

            {/* Breakdowns */}
            <Grid container spacing={3}>
                {/* Category Breakdown */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 3, height: "100%" }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 2,
                            }}
                        >
                            <CategoryIcon
                                sx={{ mr: 1, color: "primary.main" }}
                            />
                            <Typography variant="h6">By Category</Typography>
                        </Box>
                        <List>
                            {stats.category_breakdown.map((item, index) => (
                                <React.Fragment key={item.label}>
                                    <ListItem>
                                        <ListItemText
                                            primary={
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent:
                                                            "space-between",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <Typography variant="body1">
                                                        {item.label}
                                                    </Typography>
                                                    <Chip
                                                        label={`${item.percentage}%`}
                                                        size="small"
                                                        color="primary"
                                                    />
                                                </Box>
                                            }
                                            secondary={
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent:
                                                            "space-between",
                                                        mt: 1,
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        {formatCurrencyBDT(
                                                            item.value,
                                                        )}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {item.count} expenses
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                    {index <
                                        stats.category_breakdown.length - 1 && (
                                        <Divider component="li" />
                                    )}
                                </React.Fragment>
                            ))}
                        </List>
                    </Paper>
                </Grid>

                {/* Payment Method Breakdown */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 3, height: "100%" }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 2,
                            }}
                        >
                            <Payment sx={{ mr: 1, color: "secondary.main" }} />
                            <Typography variant="h6">
                                By Payment Method
                            </Typography>
                        </Box>
                        <List>
                            {stats.payment_method_breakdown.map(
                                (item, index) => (
                                    <React.Fragment key={item.label}>
                                        <ListItem>
                                            <ListItemText
                                                primary={
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent:
                                                                "space-between",
                                                            alignItems:
                                                                "center",
                                                        }}
                                                    >
                                                        <Typography variant="body1">
                                                            {item.label}
                                                        </Typography>
                                                        <Chip
                                                            label={`${item.percentage}%`}
                                                            size="small"
                                                            color="secondary"
                                                        />
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent:
                                                                "space-between",
                                                            mt: 1,
                                                        }}
                                                    >
                                                        <Typography variant="body2">
                                                            {formatCurrencyBDT(
                                                                item.value,
                                                            )}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {item.count}{" "}
                                                            transactions
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                        {index <
                                            stats.payment_method_breakdown
                                                .length -
                                                1 && <Divider component="li" />}
                                    </React.Fragment>
                                ),
                            )}
                        </List>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};
